import type { CartesianPlanningActionRequest } from '@sb/ros/CartesianPlanningAction';
import type { TaskFromRobotGoalActionRequest } from '@sb/ros/TaskFromRobotGoalAction';
import {
  roundToDecimalPlaces,
  roundToDecimalPlacesRadians,
} from '@sb/utilities';

/**
 * Fields excluded from the bucket key because they depend on the start
 * position. The bucket groups plans by goal/connection; start-position
 * matching is done via tolerance comparison instead.
 *
 * Typed against the request union so the compiler errors if a field name
 * is invalid. If you add a new start-position-dependent field to the
 * request types, add it here — the test suite will also catch it.
 */
type AllRequestKeys = keyof (TaskFromRobotGoalActionRequest &
  CartesianPlanningActionRequest);

export const BUCKET_KEY_EXCLUDED_FIELDS: ReadonlySet<AllRequestKeys> = new Set([
  'startJointState',
  'startTooltipPose',
  'startWristPose',
] as const);

const ensureArrayForCacheKey = (key: string, value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value as number[];
  }

  // Handle array-like objects (e.g. Float64Array from rclnodejs, or objects
  // with numeric indices from deserialization edge cases). Convert to a
  // regular Array so the cache key can be computed instead of crashing the
  // entire motion.
  if (value != null && typeof value === 'object' && 'length' in value) {
    return Array.from(value as ArrayLike<number>);
  }

  throw new Error(`Key '${key}' w/ value=${value} is not array-like`);
};

const transformForCacheKey = (
  key: string,
  value: unknown,
  decimalPlaces: number,
  radiansDecimalPlaces: number,
) => {
  if (
    key === 'scalingFactor' ||
    key === 'startWristPose' ||
    key === 'startTooltipPose'
  ) {
    return undefined;
  }

  if (key === 'activeEndEffectorEquipmentIds' && Array.isArray(value)) {
    return [...(value as string[])].sort().join(',');
  }

  if (['jointAngles', 'startJointState'].includes(key)) {
    const arr = ensureArrayForCacheKey(key, value);

    const rounded = arr.map((joint) =>
      roundToDecimalPlacesRadians(joint, radiansDecimalPlaces).toString(),
    );

    return `[${rounded.join(',')}]`;
  }

  if (typeof value === 'number') {
    return roundToDecimalPlaces(value, decimalPlaces);
  }

  return value;
};

/**
 * Get a ROS motion plan cache key.
 *
 * @param action Action to be cached
 * @param decimalPlaces All numbers are rounded to `decimalPlaces`
 * @param radiansDecimalPlaces Joint angles stated in radians are rounded to `radiansDecimalPlaces`.
 * @returns A cache key.
 */
export const getRosMotionPlanCacheKey = (
  action: TaskFromRobotGoalActionRequest,
  decimalPlaces = 3,
  radiansDecimalPlaces = 3,
): string => {
  return JSON.stringify(action, (k, val) =>
    transformForCacheKey(k, val, decimalPlaces, radiansDecimalPlaces),
  );
};

/**
 * Get a ROS motion plan bucket key.  For the bucket, we don't want to
 * include the start joint state.
 *
 * @param action
 * @param decimalPlaces
 * @param radiansDecimalPlaces
 * @returns
 */
export const getMotionPlanBucketKey = (
  action: TaskFromRobotGoalActionRequest,
  decimalPlaces = 3,
  radiansDecimalPlaces = 2,
): string => {
  return JSON.stringify(action, (k, val) => {
    if (BUCKET_KEY_EXCLUDED_FIELDS.has(k as AllRequestKeys)) {
      return undefined;
    }

    return transformForCacheKey(k, val, decimalPlaces, radiansDecimalPlaces);
  });
};
